import { get, BASE } from '../api.js';
import { setTopbar, lucideIcons, esc } from '../app.js';
import { navigate } from '../router.js';
import { toast } from '../dialog.js';
import { getWasm } from '../crypto.js';
import { decryptItemSummary } from '../item-decrypt.js';

export async function renderNotarizationDetail(container, params) {
  const id = params.id;
  container.innerHTML = '<p class="loading">Loading...</p>';

  try {
    const [row, cert] = await Promise.all([get(`/notarizations/${id}`), get(`/notarizations/${id}/certificate`)]);

    // Resolve linked item name
    let linkedItemName = null;
    if (row.item_id) {
      try {
        const item = await get(`/items/${row.item_id}`);
        const summary = decryptItemSummary(item);
        if (summary.name !== 'Untitled') linkedItemName = summary.name;
      } catch {}
    }

    setTopbar(linkedItemName ? `Notarization: ${linkedItemName}` : 'Notarization', '');

    const ts = new Date(row.timestamp).toLocaleString();

    const eventBadge = row.event_type === 'grant'
      ? '<span class="badge" style="background:var(--bg-elevated);color:var(--accent);font-size:11px"><i data-lucide="share-2" style="width:11px;height:11px"></i> Grant</span>'
      : '<span class="badge" style="background:var(--bg-elevated);color:var(--accent);font-size:11px"><i data-lucide="stamp" style="width:11px;height:11px"></i> Item</span>';

    let html = `
      <div class="breadcrumb" id="back-to-list"><i data-lucide="arrow-left" style="width:16px;height:16px"></i> Back to Notarizations</div>
      <div class="detail-grid">
        <div class="card">
          <div class="detail-label">Timestamp</div><div class="detail-value">${esc(ts)}</div>
          <div class="detail-label">Type</div><div class="detail-value">${eventBadge}</div>
          <div class="detail-label">Content Hash</div><div class="detail-value mono" style="font-size:12px;word-break:break-all">${esc(row.content_hash)}</div>
          ${row.blob_hash ? `<div class="detail-label">Blob Hash</div><div class="detail-value mono" style="font-size:12px;word-break:break-all;display:flex;align-items:center;gap:6px"><i data-lucide="shield-check" style="width:14px;height:14px;color:var(--success)"></i> ${esc(row.blob_hash)}</div>` : ''}
          ${row.item_id ? `<div class="detail-label">Linked Item</div><div class="detail-value"><a href="#/items/${row.item_id}">${esc(linkedItemName || row.item_id.slice(0, 8) + '...')}</a></div>` : ''}
        </div>
        <div class="card">
          <div class="detail-label">Tree Index</div><div class="detail-value mono">${row.tree_index}</div>
          <div class="detail-label">Tree Root</div><div class="detail-value mono" style="font-size:12px;word-break:break-all">${esc(row.tree_root)}</div>
          <div class="detail-label">Signature</div><div class="detail-value mono" style="font-size:11px;word-break:break-all">${esc(row.signature.slice(0, 32))}...</div>
        </div>
      </div>

      <div class="card" style="margin-top:16px">
        <div class="card-header">
          <span class="card-title">Verification</span>
          <div id="verify-status" style="font-size:13px"></div>
        </div>
        <div id="verify-detail"></div>
      </div>

      <div style="margin-top:16px;display:flex;gap:10px">
        <button class="btn btn-secondary btn-sm" id="download-cert"><i data-lucide="download" style="width:14px;height:14px"></i> Download Certificate</button>
      </div>`;

    container.innerHTML = html;
    lucideIcons();

    document.getElementById('back-to-list').onclick = () => navigate('#/notarizations');

    // Verify via server-side /verify endpoint (public, no auth needed)
    const verifyStatus = document.getElementById('verify-status');
    const verifyDetail = document.getElementById('verify-detail');

    try {
      const vres = await fetch(`${BASE}/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cert),
      });
      if (vres.ok) {
        const vdata = await vres.json();
        if (vdata.valid) {
          verifyStatus.innerHTML = '<span style="color:var(--success)"><i data-lucide="check-circle" style="width:14px;height:14px"></i> Signature valid</span>';
        } else {
          const fails = Object.entries(vdata.checks || {}).filter(([,v]) => !v).map(([k]) => k).join(', ');
          verifyStatus.innerHTML = `<span style="color:var(--danger)"><i data-lucide="x-circle" style="width:14px;height:14px"></i> Verification failed: ${esc(fails)}</span>`;
        }
      } else {
        verifyStatus.textContent = 'Server verification unavailable';
      }
    } catch (e) {
      verifyStatus.textContent = 'Verification error: ' + e.message;
    }

    if (cert.anchors && cert.anchors.length > 0) {
      let anchorHtml = '<div style="margin-top:8px"><div class="detail-label">RFC 3161 Anchors</div>';
      for (const a of cert.anchors) {
        anchorHtml += `<div style="font-size:12px;color:var(--text-secondary);margin-top:4px">${esc(a.type)} at ${new Date(a.timestamp).toLocaleString()} (tree size: ${a.tree_size})</div>`;
      }
      anchorHtml += '</div>';
      verifyDetail.innerHTML = anchorHtml;
    }

    lucideIcons();

    // Download certificate
    document.getElementById('download-cert').onclick = () => {
      const blob = new Blob([JSON.stringify(cert, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `notarization-${id.slice(0, 8)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    };
  } catch (err) {
    container.innerHTML = `<div class="msg msg-error">${esc(err.message)}</div>`;
  }
}

function hexToBytes(hex) {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < bytes.length; i++) {
    bytes[i] = parseInt(hex.substr(i * 2, 2), 16);
  }
  return bytes;
}
